// Shared eager primitives for the Lead Gen Tools landing page.
//
// These helpers are used by BOTH the above-the-fold eager module
// (LeadGenToolsLanding.tsx) AND the below-the-fold lazy chunk
// (LeadGenToolsLanding.belowFold.tsx). They live here so the lazy
// split does not duplicate them and so neither module has to import
// the other (which would defeat the code-split / create a cycle).
//
// Bodies are byte-identical to the originals that used to live inline
// in LeadGenToolsLanding.tsx.

import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';

// Shared organic ease curve from the spec
export const ORGANIC = [0.22, 1, 0.36, 1] as const;

// ─── Helpers ───────────────────────────────────────────────────────
export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export function TiltCard({
  children,
  className = '',
  maxTilt = 8,
  glow = false,
}: {
  children: React.ReactNode;
  className?: string;
  maxTilt?: number;
  glow?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.transform = `perspective(900px) rotateX(${(-y * maxTilt).toFixed(2)}deg) rotateY(${(x * maxTilt).toFixed(2)}deg) translateZ(0)`;
      if (glow) {
        el.style.setProperty('--gx', `${(x + 0.5) * 100}%`);
        el.style.setProperty('--gy', `${(y + 0.5) * 100}%`);
      }
    };
    const onLeave = () => {
      el.style.transform = 'perspective(900px) rotateX(0) rotateY(0) translateZ(0)';
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [maxTilt, reduced, glow]);
  return (
    <div
      ref={ref}
      className={`transition-transform duration-200 will-change-transform ${className}`}
      style={{ transformStyle: 'preserve-3d' }}
    >
      {children}
    </div>
  );
}

export function MagneticButton({
  children,
  onClick,
  className = '',
  radius = 90,
  strength = 14,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  radius?: number;
  strength?: number;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > radius) {
        el.style.transform = 'translate3d(0,0,0)';
        return;
      }
      const s = (1 - dist / radius) * strength;
      const a = Math.atan2(dy, dx);
      el.style.transform = `translate3d(${(Math.cos(a) * s).toFixed(2)}px, ${(Math.sin(a) * s).toFixed(2)}px, 0)`;
    };
    const onLeave = () => (el.style.transform = 'translate3d(0,0,0)');
    window.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [reduced, radius, strength]);
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`transition-transform duration-200 will-change-transform ${className}`}
    >
      {children}
    </button>
  );
}

export function CountUp({
  to,
  suffix = '',
  prefix = '',
  decimals = 0,
}: {
  to: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 70, damping: 22, mass: 1 });
  const display = useTransform(spring, (v) => `${prefix}${v.toFixed(decimals)}${suffix}`);
  useEffect(() => {
    if (inView) mv.set(to);
  }, [inView, mv, to]);
  return <motion.span ref={ref}>{display}</motion.span>;
}
